/**
 * Calcolatore di preventivi forfettari — unica fonte di verità per l'intera app.
 *
 * Regola (regime forfettario L. 190/2014):
 *   - IVA: operazione fuori campo IVA (non si applica), nessun numero di fattura IVA
 *   - Ritenuta d'acconto: assente per il regime forfettario
 *   - Rivalsa INPS / contributi: configurabile (default 4% a carico del cliente)
 *   - Imposta di bollo: €2 quando il documento supera €77,47 (DPR 642/1972)
 *     - modalità "auto": applica solo se superata soglia
 *     - modalità "virtual": indica la virtualizzazione della marca (F24 annuale)
 *
 * Aritmetica: ogni calcolo avviene internamente in CENTESIMI (interi) per evitare
 * errori di arrotondamento floating-point; l'output è espresso in euro con 2 dp.
 */

import { pctOf, round2, sumMoney, toEuros, toCents } from './money.js';
import {
  STAMP_DUTY,
  INPS_DEFAULTS,
  FORFETTARIO_LEGAL_NOTE,
  NO_WITHHOLDING_NOTE,
} from './constants.js';

export class QuoteInputError extends Error {
  constructor(message, details = null) {
    super(message);
    this.name = 'QuoteInputError';
    this.details = details;
  }
}

export function assertQuoteInput(input) {
  if (!input || typeof input !== 'object') {
    throw new QuoteInputError('Input deve essere un oggetto', { input });
  }
  const { items, discount_mode } = input;
  if (!Array.isArray(items)) {
    throw new QuoteInputError('items deve essere un array', { items });
  }
  const invalid = items.filter(
    (i, idx) =>
      typeof i !== 'object' ||
      typeof i.quantity !== 'number' ||
      typeof i.unit_price !== 'number',
  );
  if (invalid.length) {
    const indices = invalid.map((_, i) => i);
    throw new QuoteInputError(
      `Elementi item non validi (quantity/unit_price): ${indices.join(', ')}`,
      { invalid },
    );
  }
  if (discount_mode && !['none', 'percent', 'amount'].includes(discount_mode)) {
    throw new QuoteInputError(
      `discount_mode non valido: "${discount_mode}". Valori: none|percent|amount`,
      { discount_mode },
    );
  }
}

export function computeLineTotal({ quantity = 0, unit_price = 0, discount_percent = 0 }) {
  const qty = Number(quantity) || 0;
  const up = Number(unit_price) || 0;
  const disc = Math.min(100, Math.max(0, Number(discount_percent) || 0));
  return round2(qty * up * (1 - disc / 100));
}

/**
 * Costruisce il riepilogo completo di un preventivo forfettario.
 *
 * @param {Object} input
 * @param {Array} input.items
 * @param {'none'|'percent'|'amount'} [input.discount_mode]
 * @param {number} [input.discount_value=0]
 * @param {boolean} [input.inps_enabled=true]
 * @param {number} [input.inps_rate]
 * @param {boolean} [input.stamp_duty_enabled=true]
 * @param {number} [input.stamp_duty_amount]
 * @param {number} [input.stamp_duty_threshold]
 * @param {'auto'|'always'|'never'} [input.stamp_duty_mode='auto']
 * @param {string} [input.regime='forfettario']
 * @param {number} [input.vat_rate=0]
 * @returns {{subtotal, discount_amount, taxable_amount, inps_amount, stamp_duty_amount, vat_amount, total_amount, currency, lines, legal_notes}}
 */
export function computeQuoteTotals(input = {}) {
  const {
    items = [],
    discount_mode = 'none',
    discount_value = 0,
    inps_enabled = true,
    inps_rate = INPS_DEFAULTS.rate,
    stamp_duty_enabled = true,
    stamp_duty_amount = STAMP_DUTY.amount,
    stamp_duty_threshold = STAMP_DUTY.threshold,
    stamp_duty_mode = STAMP_DUTY.mode,
    regime = 'forfettario',
    vat_rate = 0,
  } = input;

  const lines = items.map((item, idx) => ({
    position: idx + 1,
    quantity: Number(item.quantity) || 0,
    unit_price: Number(item.unit_price) || 0,
    discount_percent: Number(item.discount_percent) || 0,
    line_total: computeLineTotal(item),
  }));

  const subtotal = sumMoney(lines.map((l) => l.line_total));

  let discount_amount = 0;
  if (discount_mode === 'percent') {
    discount_amount = pctOf(subtotal, Number(discount_value) || 0);
  } else if (discount_mode === 'amount') {
    discount_amount = round2(Number(discount_value) || 0);
  }

  const taxable_amount = round2(subtotal - discount_amount);
  const inps_amount = inps_enabled ? pctOf(taxable_amount, Number(inps_rate) || 0) : 0;

  const baseForStamp = taxable_amount + inps_amount;
  let stamp_duty_applied = false;
  let stamp_duty_total = 0;
  if (stamp_duty_enabled) {
    const apply =
      stamp_duty_mode === 'always' ||
      (stamp_duty_mode !== 'never' && baseForStamp > stamp_duty_threshold);
    stamp_duty_applied = apply;
    stamp_duty_total = apply ? round2(stamp_duty_amount) : 0;
  }

  const vat_amount =
    regime !== 'forfettario' && vat_rate
      ? pctOf(taxable_amount, vat_rate)
      : 0;

  const total_amount = round2(
    taxable_amount + inps_amount + stamp_duty_total + vat_amount,
  );

  const legal_notes = [];
  if (regime === 'forfettario') {
    legal_notes.push(FORFETTARIO_LEGAL_NOTE);
    legal_notes.push(NO_WITHHOLDING_NOTE);
  }
  if (stamp_duty_applied) {
    legal_notes.push(STAMP_DUTY.regulation);
  }

  return {
    subtotal,
    discount_amount,
    discount_mode,
    discount_value: Number(discount_value) || 0,
    taxable_amount,
    inps_enabled,
    inps_rate: Number(inps_rate) || 0,
    inps_amount,
    stamp_duty_applied,
    stamp_duty_amount: stamp_duty_total,
    stamp_duty_mode,
    stamp_duty_threshold,
    vat_amount,
    vat_rate: Number(vat_rate) || 0,
    total_amount,
    currency: 'EUR',
    lines,
    legal_notes,
  };
}

export function formatQuoteNumber(pattern, { year, seq } = {}) {
  const y = String(year ?? new Date().getFullYear());
  const m = new Date().toISOString().slice(5, 7);
  const s = String(seq ?? 1).padStart(4, '0');
  const date = `${y}${m}${String(new Date().getDate()).padStart(2, '0')}`;
  return pattern
    .replaceAll('{year}', y)
    .replaceAll('{yyyy}', y)
    .replaceAll('{mm}', m)
    .replaceAll('{seq:4}', s)
    .replaceAll('{seq}', String(seq ?? 1))
    .replaceAll('{date}', date);
}

